import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import theme from '@theme';
import Card from '@designSystem/Card';
import { useForm, SubmitHandler } from 'react-hook-form';
import Button from '@designSystem/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import InformationBlock from '@designSystem/InformationBlock';
import toast, { LoaderIcon } from 'react-hot-toast';
import axios from 'axios';
import { env } from '../config';

const Container = styled(Card)`
  margin: 2rem auto;
  padding: 2rem;
  border-radius: 8px;
  font-size: ${theme.fonts.size.base};
  width: 700px;
`;

const Title = styled.div`
  font-size: ${theme.fonts.size.base};
  color: ${theme.palette.grey[600]};
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const SubTitle = styled.div`
  font-size: ${theme.fonts.size.base};
  color: ${theme.palette.grey[400]};
  font-weight: 400;
  margin-bottom: 0.5rem;
`;

const FormGroup = styled.div`
  width: 100%;
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: ${theme.fonts.size.sm};
  color: ${theme.palette.grey[600]};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.3rem;
  border: 1px solid ${theme.palette.grey[300]};
  border-radius: 4px;
  font-size: ${theme.fonts.size.sm};
  &:focus {
    border-color: ${theme.palette.blue[500]};
    outline: none;
  }
  &::placeholder {
    color: ${theme.palette.grey[400]};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${theme.palette.grey[300]};
  border-radius: 4px;
  font-size: ${theme.fonts.size.sm};
  &:focus {
    border-color: ${theme.palette.blue[500]};
    outline: none;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${theme.palette.grey[300]};
  border-radius: 4px;
  font-size: ${theme.fonts.size.sm};
  &:focus {
    border-color: ${theme.palette.blue[500]};
    outline: none;
  }
`;

const ErrorMessage = styled.span`
  color: ${theme.palette.red[500]};
  font-size: ${theme.fonts.size.sm};
`;

export interface ContactInputs {
  username: string;
  phone: string;
  email: string;
  issueType: string;
  product: string;
  address: string;
  buildingId?: string;
  orderReference?: string;
  comment: string;
}

interface ContactFormManagerProps {
  children: React.ReactNode;
  isVisible?: boolean;
  label: string;
}

const ContactFormManager: React.FC<ContactFormManagerProps> = ({
  children,
  isVisible = true,
  label
}) => {
  return (
    <div style={{ display: isVisible ? 'block' : 'none', width: '100%' }}>
      <FormGroup>
        <Label>{label}</Label>
        {children}
      </FormGroup>
    </div>
  );
};

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const defaultValues = {
    phone: queryParams.get('phone') || '',
    email: queryParams.get('email') || '',
    issueType: queryParams.get('issueType') || 'order',
    product: queryParams.get('product') || '',
    address: queryParams.get('address') || '',
    buildingId: queryParams.get('buildingId') || '',
    orderReference: queryParams.get('orderReference') || '',
    comment: queryParams.get('comment') || ''
  };

  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<ContactInputs>({ defaultValues });
  const watchIssueType = watch('issueType');

  const onSubmit: SubmitHandler<ContactInputs> = async (data) => {
    setIsLoading(true);
    try {
      const formData = new FormData();

      (Object.keys(data) as Array<keyof ContactInputs>).forEach((property) => {
        const value = data[property];

        if (value !== undefined) {
          formData.append(property, value.toString());
        }
      });

      const response = await axios.post(`${env.API_URL}/support`, {
        data
      });

      if (response.status === 200) {
        setIsLoading(false);
        navigate('/ordersBeta/tracking');
        toast.success('Votre demande a bien été envoyée. Nous vous recontacterons rapidement.');
      }
    } catch (error) {
      setIsLoading(false);
      toast.error("Une erreur s'est produite pendant l'envoi.");
    }
  };

  return (
    <Container className="main-content-container">
      <Title>Demande de support</Title>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ContactFormManager label={t('contact.issueType', 'Mon problème concerne')}>
          <Select {...register('issueType', { required: true })}>
            <option value="">{t('contact.select', 'Sélectionnez...')}</option>
            <option value="order">{t('contact.issueType.orders', 'Commandes')}</option>
            <option value="user">
              {t('contact.issueType.account.user', 'Compte utilisateur')}
            </option>
          </Select>
          {errors.issueType && (
            <ErrorMessage>{t('contact.errors.required', 'Ce champ est requis')}</ErrorMessage>
          )}
        </ContactFormManager>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          <ContactFormManager
            label={t('contact.phone', 'Téléphone contact')}
            isVisible={watchIssueType === 'order'}
          >
            <Input
              type="tel"
              placeholder={t('contact.phonePlaceholder', '0633285654')}
              {...register('phone', { required: true })}
            />
            {errors.phone && (
              <ErrorMessage>{t('contact.errors.required', 'Ce champ est requis')}</ErrorMessage>
            )}
          </ContactFormManager>

          <ContactFormManager
            label={t('contact.email', 'Email')}
            isVisible={watchIssueType === 'order'}
          >
            <Input
              type="email"
              placeholder={t('contact.emailPlaceholder', 'email@gmail.com')}
              {...register('email', { required: true })}
            />
            {errors.email && (
              <ErrorMessage>{t('contact.errors.required', 'Ce champ est requis')}</ErrorMessage>
            )}
          </ContactFormManager>
        </div>

        {watchIssueType === 'order' && (
          <SubTitle>{t('contact.complementary', 'Information complémentaire')}</SubTitle>
        )}

        <ContactFormManager
          label={t('contact.product', 'Produit commandé')}
          isVisible={watchIssueType === 'order'}
        >
          <Input
            placeholder={t('contact.productPlaceholder', 'Produit')}
            {...register('product', { required: true })}
          />
          {errors.product && (
            <ErrorMessage>{t('contact.errors.required', 'Ce champ est requis')}</ErrorMessage>
          )}
        </ContactFormManager>

        <ContactFormManager
          label={t('contact.address', "Adresse de l'accès")}
          isVisible={watchIssueType === 'order'}
        >
          <Input
            placeholder={t('contact.addressPlaceholder', '12 rue Machiavel - 75016 Paris')}
            {...register('address', { required: true })}
          />
          {errors.address && (
            <ErrorMessage>{t('contact.errors.required', 'Ce champ est requis')}</ErrorMessage>
          )}
        </ContactFormManager>

        <ContactFormManager
          label={t('contact.buildingId', 'Identifiant Immeuble (facultatif)')}
          isVisible={watchIssueType === 'order'}
        >
          <Input
            placeholder={t('contact.buildingIdPlaceholder', '1234')}
            {...register('buildingId')}
          />
        </ContactFormManager>

        <ContactFormManager
          label={t('contact.orderReference', 'Votre référence de commande (facultatif)')}
          isVisible={watchIssueType === 'order'}
        >
          <Input
            placeholder={t('contact.orderReferencePlaceholder', '123456789')}
            {...register('orderReference')}
          />
        </ContactFormManager>

        <ContactFormManager
          label={t('contact.comment', 'Commentaire')}
          isVisible={watchIssueType === 'order'}
        >
          <Textarea
            placeholder={t(
              'contact.commentPlaceholder',
              'Merci de nous détailler le problème que vous rencontrez. Par exemple, à quelle étape du parcours avez-vous rencontré une difficulté. N’hésitez pas apporter le maximum de contexte.'
            )}
            {...register('comment', { required: true })}
          ></Textarea>
          {errors.comment && (
            <ErrorMessage>{t('contact.errors.required', 'Ce champ est requis')}</ErrorMessage>
          )}
        </ContactFormManager>
        {watchIssueType !== 'order' && (
          <InformationBlock
            style={{ marginBottom: '10px' }}
            title="Veuillez contacter le support au 0800 740 790 en cas de problème de 8h à 18h."
          ></InformationBlock>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button design="white" type="button" onClick={() => navigate(-1)}>
            {t('contact.return', 'Retour')}
          </Button>
          {watchIssueType === 'order' && (
            <Button type="submit" disabled={isLoading}>
              {!isLoading ? t('contact.submit', 'Envoyer') : <LoaderIcon />}
            </Button>
          )}
        </div>
      </form>
    </Container>
  );
};

export default Contact;
